import { supertypeClass, amorphicStatic, remote } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Customer } from '../../../common/js/customer/Customer';
import { Policy } from '../../../common/js/customer/Policy';
import * as path from 'path';
import { Person } from '../../../common/js/customer/Person';
import { Application } from '../../../common/js/customer/Application';
import { RabbitmqPublisher } from '../../../common/js/rabbitmqPublisher/RabbitmqPublisher';
import { ElasticSchema } from '@hit/hit-schemas';
import { Diagnosis } from '../../../common/js/customer/Diagnosis';
import { DiagnosisShort } from '../../../common/js/customer/DiagnosisShort';
import { DiagnosisExtra } from '../../../common/js/customer/DiagnosisExtra';
import { FamilyCondition } from '../../../common/js/customer/FamilyCondition';
import { Avocation } from '../../../common/js/customer/Avocation';
import { AccidentAtFault } from '../../../common/js/customer/AccidentAtFault';
import { SpeedingTicket } from '../../../common/js/customer/SpeedingTicket';
import { RecklessTicket } from '../../../common/js/customer/RecklessTicket';
import { OtherMovingViolation } from '../../../common/js/customer/OtherMovingViolation';
import { DrivingWhileSuspendedCon } from '../../../common/js/customer/DrivingWhileSuspendedCon';
import { TravelCountry } from '../../../common/js/customer/TravelCountry';
import { BigGameCountry } from '../../../common/js/customer/BigGameCountry';
import { ExistingPolicy } from '../../../common/js/customer/ExistingPolicy';
import { RX } from '../../../common/js/customer/RX';
import { Surgery } from '../../../common/js/customer/Surgery';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { CommonUtils } from '../../../common/js/static/CommonUtils';
import { ExistingPolicyInForce } from '../../../common/js/customer/ExistingPolicyInForce';
import { ExistingPolicyApplying } from '../../../common/js/customer/ExistingPolicyApplying';
import { PartyRelation } from '../../../common/js/customer/PartyRelation';
import { Trustee } from '../../../common/js/customer/Trustee';
import { Grantor } from '../../../common/js/customer/Grantor';
import { ResidentialAsset } from '../../../common/js/customer/ResidentialAsset';
import { BusinessPurposePartners } from '../../../common/js/customer/PolicyBusinessPurpose';
import { CoverpathPolicy } from '../../classes/coverpath_subclasses/CoverpathPolicy';
import { Product } from '../../../common/js/static/Product';
import { CoverpathCustomer } from '../../classes/coverpath_subclasses/CoverpathCustomer';
import { MMDirectCustomer } from '../../classes/mmdirect_subclasses/MMDirectCustomer';
import { Utils } from '../../../common/js/Utils';
import { AdvisorInfoElastic } from '../../../common/js/elastic/AdvisorInfoElastic';
import {
  agentToAdvisor,
  getAdditionalAdvisors,
  getInsured,
  getJuvieSigner,
  getOtherOwner,
  getOwner,
  PersonElastic,
} from '../../../common/js/elastic/ElasticUtils';
import { createHmac } from 'crypto';
import { CasApplication } from '../../classes/coverpath_subclasses/CasApplication';
import { LoggerSingleton, Category } from '../../../common/js/logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class ApplicationController extends Everything {
  constructor() {
    super();
  }

  static new() {
    return new ApplicationController().init();
  }

  init() {
    return this;
  }

  async submitPolicy(customer: CoverpathCustomer | MMDirectCustomer): Promise<void> {
    const policy = customer.application.primaryPolicy;
    CoverpathUtils.copyInsuredToOwner(policy, customer);
    CommonUtils.copySpouseInfo(policy);
    // Mass Mutual owns all LifeBridge policies, so customer is never the owner
    if (!policy.isLifeBridge()) {
      CoverpathUtils.copyCustomerEmailToOwner(policy, customer);
    }

    const advisor: boolean = policy.getChannelCode() === Product.channels.CAS.code;
    const error = await customer.submitPolicy();
    if (error) {
      return error;
    }
    await this.elasticPut(policy, true, advisor);
  }

  @remote({ on: 'server' })
  async elasticPut(policy: Policy, reindex?: boolean, cp?: boolean): Promise<void> {
    let primaryAdvisor: AdvisorInfoElastic = null;
    if (cp) {
      primaryAdvisor = agentToAdvisor(policy.application, policy.primaryPolicyAgent);
    }
    if (!policy.insuredType || policy.insuredType === 'you') {
      await this.elasticReindexServer(policy.customer, policy, 'Owner/Insured', null, primaryAdvisor);
    } else {
      //do include insured person
      const insured: PersonElastic = await getInsured(policy);
      await this.elasticReindexServer(policy.customer, policy, 'Owner', insured, primaryAdvisor);
    }
  }

  @remote({ on: 'server' })
  async elasticReindexServer(
    customer,
    policy: Policy,
    role: string,
    insured,
    advisor: AdvisorInfoElastic
  ): Promise<void> {
    if (!this.amorphic.config || !this.amorphic.config.nconf.get('summaryPublisherEnabled')) {
      return;
    }

    const customerId = customer.primaryCustomer.customerPersistor.id;

    const policyNum = policy.policyNumber;

    const owner: PersonElastic = await getOwner(policy, role);

    const advisorList: AdvisorInfoElastic[] = getAdditionalAdvisors(policy);

    const otherOwner: PersonElastic = await getOtherOwner(policy);

    const juvieSigner = await getJuvieSigner(policy);

    const elasticData = {
      eventType: 'summary',
      id: policyNum,
      customerId: customerId,
      firstName: owner.firstName,
      lastName: owner.lastName,
      middleName: owner.middleName,
      email: owner.email,
      emailSearch: owner.emailSearch,
      dob: owner.dob,
      role: role,
      ssn: owner.ssn,
      ssnLastFour: owner.ssnLastFour,
      state: owner.state,
      policy: policy.policyNumber,
      policyLastFour: policy.policyNumber.slice(-4),
      channel: policy.getChannelCode(),
      covered: insured,
      otherOwner: otherOwner,
      advisor: advisor,
      advisorList: advisorList,
      ownerClassification: policy.ownerClassification,
      trustOrEntity: policy.trustOrOtherEntity ? policy.trustOrOtherEntity.type : null,
      name: null,
      govtId: null,
      applicationId: policy.application._id,
      usesUnderwriting: policy.usesUnderwriting(),
      juvieSigner: juvieSigner,
      product: policy.product,
    };

    if (policy.ownerEntity) {
      elasticData.name = policy.ownerEntity.getFullNameOfOwner();
      elasticData.govtId = policy.ownerEntity.taxId || '';
      elasticData.state = policy.ownerEntity.trustSitus || '';
      elasticData.dob = null;
      elasticData.firstName = null;
      elasticData.middleName = null;
      elasticData.lastName = null;
      elasticData.ssn = 'None';
    }

    LoggerSingleton.getInstance().info(
      {
        module: 'ApplicationController',
        function: 'elasticReindexServer',
        data: {
          policyNumber: policyNum,
          AdvisorCount: advisorList.length,
        },
        category: Category.request,
      },
      `Additional advisor list`
    );

    RabbitmqPublisher.singleton().publish(elasticData, ElasticSchema);

    const elasticDelete = {
      eventType: 'summary',
      channel: policy.getChannelCode(),
      id: customerId,
      delete: true,
    };
    return RabbitmqPublisher.singleton().publish(elasticDelete, ElasticSchema);
  }

  createIterateClass(className, customer: Customer) {
    switch (className) {
      case 'FamilyCondition':
        return FamilyCondition.new(customer);
      case 'Avocation':
        return Avocation.new(customer);
      case 'AccidentAtFault':
        return AccidentAtFault.new(customer);
      case 'SpeedingTicket':
        return SpeedingTicket.new(customer);
      case 'RecklessTicket':
        return RecklessTicket.new(customer);
      case 'OtherMovingViolation':
        return OtherMovingViolation.new(customer);
      case 'DrivingWhileSuspendedCon':
        return DrivingWhileSuspendedCon.new(customer);
      case 'TravelCountry':
        return TravelCountry.new(customer);
      case 'BigGameCountry':
        return BigGameCountry.new(customer);
      case 'PartyRelation':
        return PartyRelation.new(customer);
      case 'ExistingPolicy':
        return ExistingPolicy.new(customer);
      case 'ExistingPolicyInForce':
        return ExistingPolicyInForce.new(customer);
      case 'ExistingPolicyApplying':
        return ExistingPolicyApplying.new(customer);
      case 'RX':
        return RX.new(customer);
      case 'Surgery':
        return Surgery.new(customer);
      case 'DiagnosisShort':
        return DiagnosisShort.new(customer);
      case 'BusinessPurposePartners':
        return BusinessPurposePartners.new(customer);
      case 'Diagnosis':
        return Diagnosis.new(customer);
      case 'DiagnosisExtra':
        return DiagnosisExtra.new(customer);
      case 'Trustee':
        return Trustee.new(customer);
      case 'Grantor':
        return Grantor.new(customer);
      case 'ResidentialAsset':
        return ResidentialAsset.new(customer);
      default:
        UILoggerSingleton.getInstance().error(
          {
            module: 'ApplicationController',
            function: 'createIterateClass',
            category: UICategory.request,
            error: {
              isHumanRelated: false,
            },
          },
          `iterate-collection class: ${className} was not found`
        );
    }
  }
}
